/**
 * 编辑器正文的**唯一**「内容 → 编辑器 HTML」转换入口。
 *
 * ── 为什么要有这个文件（真实事故）──────────────────────────────
 * "按 \n 分段、每段包 `<p>`" 这段逻辑曾被复制到三处：章节编辑器加载内容、
 * Harness 引擎 execute 之后回写编辑器、旧 AI 循环 execute 之后回写编辑器。
 * 只有章节编辑器那一份带了「内容已经是 HTML 就直接返回」的判断，两处 AI 回写没有 ——
 * 于是 AI 发来的 `<p>雪停了。</p><p>他推开窗。</p>` 被**整体又包了一层**：
 *
 *   `<p><p>雪停了。</p><p>他推开窗。</p></p>`
 *
 * 浏览器 HTML 解析器（TipTap `setContent(string)` 走的也是它）对 `<p><p>` 的处理是
 * 「隐式闭合」：外层 `<p>` 立刻被关掉、尾部多出来的 `</p>` 又补出一个空段落，得到
 *
 *   `<p></p><p>雪停了。</p><p>他推开窗。</p><p></p>`
 *
 * 也就是用户看到的「正文开头和末尾各多出一对 P 标签（上下各多一个空行）」。
 * 更糟的是 AI 回写后紧接着 `onAutoSave()` 会把 `editor.getHTML()` 存回数据库，
 * 于是脏内容被持久化，重新打开章节依然多两个空段落。
 *
 * ── 判定依据（幂等）──────────────────────────────────────────
 * 判据：内容里**是否已含块级标签**。含块级标签 ⇒ 它已经是正文 HTML（本项目的存储约定
 * 就是 `<p>段落</p><p>段落</p>`），原样返回，不再包装；否则才是旧数据/模型给的纯文本，
 * 按 `/\n+/` 分段包 `<p>`（空行 → `<p><br></p>`，与历史行为保持一致）。
 *
 * 这样函数满足 `f(f(x)) === f(x)`：无论调用多少次都不会多包一层，
 * 并且**新建章节（走 ChapterEditor 加载）与 AI 更新章节（走回写）用同一个判据**，
 * 不会再出现"创建正常、更新多包一层"的分裂。
 */

/**
 * 块级标签：出现任意一个就认定内容已经是正文 HTML。
 * 有意保留 `br`（历史判据如此）：`a<br>b` 这类内联换行也属于"已经是 HTML"。
 */
const BLOCK_TAG_RE = /<(p|br|div|h[1-6]|ul|ol|li|blockquote|pre|hr|table|thead|tbody|tr|td|th|figure|figcaption)\b/i;

/**
 * 把章节正文（可能是纯文本，也可能是已经是 HTML）转成编辑器可用的 HTML。
 * **幂等**：已是 HTML 的内容原样返回，不会多包一层 `<p>`。
 *
 * 空内容返回空串：调用方按需兜底成 `<p><br></p>`（见 `writeChapterContentToEditor`）。
 */
export function plainTextToHtml(text: string): string {
  if (!text || !text.trim()) return '';

  // 情况一：已经是 HTML（含块级标签）→ 原样返回。
  // 去掉首尾空白：块级标签之外的换行/空格在正文里没有意义，留着只会让
  // "编辑器当前内容 === 已保存基线"这类比较被换行抖动误判。
  if (BLOCK_TAG_RE.test(text)) return text.trim();

  // 情况二：纯文本 → 按连续换行分段，每段用 <p> 包裹
  // 使用 /\n+/ 将连续换行视为单个段落分隔符，避免 \n\n 产生多余空段落
  const lines = text.split(/\n+/);
  const htmlParts = lines
    .map(line => {
      const trimmed = line.trim();
      return trimmed ? `<p>${trimmed}</p>` : '<p><br></p>';
    })
    .join('');

  return htmlParts || '<p><br></p>';
}

/**
 * 写回编辑器所需的最小 TipTap 接口。
 *
 * 只声明真正用到的方法：`@tiptap/react` 的 `Editor` 结构上满足它，
 * 单测里传一个假编辑器也能精确断言"到底把什么 HTML 塞进了编辑器"。
 */
export interface EditorContentTarget {
  commands: {
    setContent(content: string, options: { emitUpdate: boolean }): unknown;
  };
}

/**
 * 把「服务端已落库的章节正文」回写到当前编辑器。
 *
 * - 内容转换一律走 `plainTextToHtml`（幂等，已 HTML 不再包一层）；
 * - `emitUpdate: false`：内容已经落库，程序化回写不应触发 `onUpdate`，
 *   否则会被界面误判为"用户有新输入未保存"，进而触发自动保存/草稿快照
 *   （历史事故：离开再返回提示"未保存的草稿"）。
 *
 * 返回是否真的写入了（编辑器为空时返回 false，调用方据此决定要不要连带自动保存）。
 */
export function writeChapterContentToEditor(
  editor: EditorContentTarget | null | undefined,
  content: string,
): boolean {
  if (!editor) return false;
  // 空正文兜底一个空段落，保持与历史行为一致（历史两处回写都写 '<p><br></p>'）
  const html = plainTextToHtml(content ?? '') || '<p><br></p>';
  editor.commands.setContent(html, { emitUpdate: false });
  return true;
}
